import React, { ChangeEvent, ComponentType, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useController, useFormContext } from 'react-hook-form'
import { Col, Form, Row } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import classNames from 'classnames'

import { BaseInputTemplateProps, FieldTemplateProps, RJSFSchema, RJSFValidationError, WidgetProps } from '@rjsf/utils'
import BaseInputTemplate from '@rjsf/core/lib/components/templates/BaseInputTemplate'
import RJSFForm, { FormProps, IChangeEvent } from '@rjsf/core'
import validator from '@rjsf/validator-ajv8'

import { SelectDropdown, SelectOption } from '../../components/SelectDropdown/SelectDropdown'
import { FormFeedback } from '../../components/Form/FormFeedback'

import { useSelectContactFormData } from './hooks'
import { filterFieldsWithoutSchema, filterHiddenFields } from './utils'

import '../../apps/professional-app/fa/MeetingSummary/CRM/UpdateCRMContactModal.scss'
import { useAppSelector } from '../../store/hooks'
import { selectContactChange } from '../../store/meetingSummary/contactUpdate'
import { formatDate } from '../../apps/professional-app/fa/common/aifeatures/utils'
import { Button } from '../../components/Button/Button'
import { getIntegrationCategoryDisplayNameByServiceId } from '../../common/integrations'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'

interface UpdateExternalContactFormProps {
  serviceId: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  contactSchema: { [key: string]: any }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  previousContactValues: { [key: string]: any }
  FooterComponent?: ComponentType
  onKeySelected?: (selectedKey: string, isUnselect: boolean) => void
}

export const UpdateExternalContactForm: React.FC<UpdateExternalContactFormProps> = ({
  serviceId,
  contactSchema,
  previousContactValues,
  FooterComponent,
  onKeySelected,
}) => {
  const { watch } = useFormContext()
  const contactValues = watch('contactValues')

  const integrationTypeDisplayName = getIntegrationCategoryDisplayNameByServiceId(serviceId)

  const { isSelected: isAllSelected, handleSelect: handleSelectAll } = useSelectContactFormData(serviceId, contactValues, ['contactValues'], onKeySelected)

  const filteredFields = useMemo(() => {
    if (!contactValues || !contactSchema) {
      return []
    }
    return Object.entries(contactValues)
      .filter((entry) => filterHiddenFields(serviceId, entry))
      .filter(filterFieldsWithoutSchema(contactSchema))
  }, [contactSchema, contactValues, serviceId])

  return (
    <Row>
      <Col className='d-flex flex-column gap-2'>
        <Row className='p-2'>
          <Col>
            <Row className='d-flex align-items-center zocks-gap-0.5'>
              <Col className='p-0' xs='auto'>
                <Form.Check type='checkbox' checked={isAllSelected} onChange={handleSelectAll} className='d-flex contact-information-checkbox' />
              </Col>
              <Col className='p-2 rounded-top rounded-1 current-contact-information'>
                <span className='lead-small'>{integrationTypeDisplayName} Contact Data</span>
              </Col>
              <Col className='p-2 rounded-top rounded-1 detected-contact-information'>
                <span className='lead-small'>New {integrationTypeDisplayName} Contact Data</span>
              </Col>
            </Row>
          </Col>
        </Row>
        {filteredFields.map(([key, value]) => {
          const rowNameParts = ['contactValues', key]

          return (
            <UpdateCRMContactFormRow
              key={rowNameParts.join('.')}
              serviceId={serviceId}
              name={key}
              nameParts={rowNameParts}
              schema={contactSchema.properties[key]}
              data={value}
              previousData={previousContactValues[key]}
              onKeySelected={onKeySelected}
            />
          )
        })}
        {FooterComponent ? <FooterComponent /> : null}
      </Col>
    </Row>
  )
}

export const UpdateExternalContactFormFooterComponent = () => {
  const contactChange = useAppSelector(selectContactChange)

  const { setValue } = useFormContext()

  const handleResetAll = useCallback(() => {
    if (!contactChange) {
      return
    }
    setValue('contactValues', contactChange.contactValues)
  }, [contactChange, setValue])

  if (!contactChange) {
    return null
  }

  return (
    <Row>
      <Col className='d-flex justify-content-between'>
        {contactChange.updatedAt ? <div className='label-small'>Last updated at {formatDate(contactChange.updatedAt)}</div> : null}
        <Button className='button-small p-0' onClick={handleResetAll}>
          <i className='bi bi-arrow-clockwise fs-6' />
          <span>Reset all</span>
        </Button>
      </Col>
    </Row>
  )
}

interface UpdateCRMContactFormRowProps {
  serviceId: string
  name: string
  nameParts: string[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schema: { [key: string]: any }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  previousData: any
  onKeySelected?: (selectedKey: string, isUnselect: boolean) => void
  indent?: boolean
  hideTitle?: boolean
  isRequired?: boolean
}

const UpdateCRMContactFormRow: React.FC<UpdateCRMContactFormRowProps> = ({
  serviceId,
  name,
  nameParts,
  schema,
  data,
  previousData,
  onKeySelected,
  indent,
  hideTitle,
  isRequired,
}) => {
  const { control } = useFormContext()

  const fieldName = useMemo(() => nameParts.join('.'), [nameParts])

  const { isSelected, handleSelect } = useSelectContactFormData(serviceId, data, nameParts, onKeySelected)

  if (!schema) {
    return null
  }

  if (schema.type === 'array') {
    return (
      <UpdateCRMContactFormArrayRow
        serviceId={serviceId}
        data={data}
        name={name}
        nameParts={nameParts}
        schema={schema}
        previousData={Array.isArray(previousData) ? previousData : null}
        onKeySelected={onKeySelected}
      />
    )
  }

  if (schema.type === 'object') {
    return (
      <UpdateCRMContactFormObjectRow
        serviceId={serviceId}
        name={name}
        nameParts={nameParts}
        schema={schema}
        data={data}
        previousData={previousData}
        onKeySelected={onKeySelected}
        hideTitle={hideTitle}
      />
    )
  }

  const formProps: FormProps = {
    validator,
    schema,
    showErrorList: false,
    uiSchema: {
      'ui:submitButtonOptions': { norender: true },
      'ui:emptyValue': getSchemaEmptyValue(schema),
    },
    templates: {
      FieldTemplate: CustomFieldTemplate,
      UnsupportedFieldTemplate: CustomUnsupportedFieldTemplate,
      BaseInputTemplate: CustomBaseInputTemplate,
    },
    widgets: {
      CheckboxWidget: CustomCheckboxWidget,
      DateWidget: CustomDateWidget,
      SelectWidget: CustomSelectWidget,
    },
  }

  return (
    <Row className='d-flex zocks-gap-0.5 p-2 contact-information-field rounded-1'>
      {indent && (
        <Col className='p-0' xs='auto'>
          <div className='leaf-contact-information-field-icon' />
        </Col>
      )}
      <Col className='p-0' xs='auto'>
        <Form.Check className='d-flex contact-information-checkbox' type='checkbox' checked={isSelected} onChange={handleSelect} />
      </Col>
      <Col className='p-0'>
        <Row>
          <Col>
            <Form.Label className='label-small'>{schema.title ?? name}</Form.Label>
          </Col>
        </Row>
        <Row>
          <Col>
            <RJSFForm {...formProps} formData={previousData} disabled={true} />
          </Col>
        </Row>
      </Col>
      <Col className='p-0'>
        <Row>
          <Col>
            <Form.Label className='label-small'>
              {schema.title ?? name}
              {isRequired ? '*' : ''}
            </Form.Label>
          </Col>
        </Row>
        <Row>
          <Col>
            <Controller
              control={control}
              name={fieldName}
              render={({ fieldState }) => (
                <>
                  <UpdateCRMContactModalFormField formProps={formProps} fieldName={fieldName} isSelected={isSelected} serviceId={serviceId} />
                  {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                </>
              )}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  )
}

const UpdateCRMContactFormArrayRow: React.FC<UpdateCRMContactFormRowProps> = ({ serviceId, name, nameParts, schema, data, previousData, onKeySelected }) => {
  const { isSelected, handleSelect } = useSelectContactFormData(serviceId, data, nameParts, onKeySelected)

  return (
    <Row>
      <Col className='p-2 d-flex flex-column contact-information-array-field zocks-gap-0.5'>
        <Row>
          <Col className='d-flex align-items-center zocks-gap-0.5'>
            <Form.Check className='d-flex contact-information-checkbox' type='checkbox' checked={isSelected} onChange={handleSelect} />
            <Form.Label className='label-small'>{schema.title ?? name}</Form.Label>
          </Col>
        </Row>
        <Row>
          <Col className='px-4 d-flex flex-column zocks-gap-0.5'>
            {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              data.map((item: any, itemIndex: number) => {
                const rowNameParts = [...nameParts, `${itemIndex}`]

                return (
                  <UpdateCRMContactFormRow
                    key={rowNameParts.join('.')}
                    serviceId={serviceId}
                    name={name}
                    nameParts={rowNameParts}
                    schema={schema.items}
                    data={item}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    previousData={(item.id && previousData && previousData.find((e: any) => e.id === item.id)) ?? null}
                    onKeySelected={onKeySelected}
                    hideTitle={true}
                    indent={true}
                  />
                )
              })
            }
          </Col>
        </Row>
      </Col>
    </Row>
  )
}

const UpdateCRMContactFormObjectRow: React.FC<UpdateCRMContactFormRowProps> = ({
  serviceId,
  name,
  nameParts,
  schema,
  data,
  previousData,
  hideTitle,
  onKeySelected,
}) => {
  const { isSelected, handleSelect } = useSelectContactFormData(serviceId, data, nameParts, onKeySelected)

  const requiredFields = schema.required ?? []

  const filteredFields = useMemo(() => {
    return Object.entries(data)
      .filter((entry) => filterHiddenFields(serviceId, entry))
      .filter(filterFieldsWithoutSchema(schema))
  }, [data, schema, serviceId])

  return (
    <Row>
      <Col className='p-2 contact-information-object-field rounded-1'>
        <Row>
          <Col className='d-flex align-items-center zocks-gap-0.5'>
            <Form.Check className='d-flex contact-information-checkbox' type='checkbox' checked={isSelected} onChange={handleSelect} />
            {!hideTitle && <Form.Label className='label-small'>{schema.title ?? name}</Form.Label>}
          </Col>
        </Row>
        <Row>
          <Col className='px-4'>
            {filteredFields.map(([key, value]) => {
              const rowNameParts = [...nameParts, key]

              return (
                <UpdateCRMContactFormRow
                  key={rowNameParts.join('.')}
                  serviceId={serviceId}
                  name={key}
                  nameParts={rowNameParts}
                  schema={schema.properties[key]}
                  data={value}
                  previousData={(previousData && previousData[key]) ?? null}
                  onKeySelected={onKeySelected}
                  indent={true}
                  isRequired={requiredFields.includes(key)}
                />
              )
            })}
          </Col>
        </Row>
      </Col>
    </Row>
  )
}

const UpdateCRMContactModalFormField: React.FC<{
  fieldName: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formProps: any
  isSelected: boolean
  serviceId: string
}> = ({ fieldName, formProps, isSelected, serviceId }) => {
  const { control, setError, clearErrors } = useFormContext()
  const { field } = useController({ name: fieldName, control })
  const [fieldError, setFieldError] = useState<string | null>(null)
  const ref = useRef<RJSFForm | null>(null)
  const { trackingClient } = useTracking()
  const [editedFields, setEditedFields] = useState<string[]>([])
  useEffect(() => {
    if (!isSelected || !fieldError) {
      clearErrors(fieldName)
      return
    }
    setError(fieldName, { type: 'string', message: fieldError })
  }, [isSelected, fieldError, fieldName, setError, clearErrors])

  const handleChange = (e: IChangeEvent, id?: string) => {
    field.onChange(e.formData)
    if (id !== undefined && !editedFields.includes(id)) {
      trackingClient.track({
        eventName: TrackEvent.CONTACT_UPDATE_RESULTS_EDITED,
        eventDetails: {
          serviceId,
          fieldId: id,
        },
      })
      const updatedEditedFields = [...editedFields, id]
      setEditedFields(updatedEditedFields)
    }
    if (!ref.current) {
      return
    }
    const isValid = ref.current.validateForm()
    if (isValid) {
      setFieldError(null)
    }
  }

  const handleError = (validationErrors: RJSFValidationError[]) => {
    setFieldError(validationErrors[0].message ?? null)
  }

  return <RJSFForm {...formProps} ref={ref} formData={field.value} onChange={handleChange} onError={handleError} />
}

const CustomFieldTemplate = (props: FieldTemplateProps) => {
  return <>{props.children}</>
}

const CustomUnsupportedFieldTemplate = () => {
  return <></>
}

export const CustomBaseInputTemplate = (props: BaseInputTemplateProps) => {
  return (
    <BaseInputTemplate
      {...props}
      className={classNames('form-control', { 'disabled-contact-information-field border-0': props.disabled })}
      placeholder='Not set'
    />
  )
}

export const CustomDateWidget = (props: WidgetProps) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)

  const parseDate = useCallback(
    (value: string | null | undefined) => {
      if (!value) {
        return null
      }
      const date = new Date(value)
      if (isNaN(date.getTime())) {
        return null
      }
      const formattedValue = date.toISOString().split('T')[0]
      if (formattedValue !== value) {
        props.onChange(formattedValue)
      }
      return date
    },
    [props],
  )

  useEffect(() => {
    if (!selectedDate) {
      setSelectedDate(parseDate(props.value))
    }
  }, [props, selectedDate, parseDate])

  const handleChange = (newValue: Date | null) => {
    if (!newValue) {
      return
    }
    const formattedValue = newValue.toISOString().split('T')[0]
    props.onChange(formattedValue)
    setSelectedDate(newValue)
  }

  return (
    <DatePicker
      className={classNames('form-control', { 'disabled-contact-information-field border-0': props.disabled })}
      wrapperClassName='w-100'
      selected={selectedDate}
      onChange={handleChange}
      todayButton='Today'
      placeholderText='Not set'
    />
  )
}

export const CustomSelectWidget = (props: WidgetProps) => {
  const options =
    props.options.enumOptions?.map((enumOption, index) => ({
      id: `${index}`,
      label: enumOption.label,
    })) ?? []

  const handleOptionSelected = (option: SelectOption) => {
    if (!props.options.enumOptions) {
      return
    }
    const optionIndex = parseInt(option.id)
    props.onChange(props.options.enumOptions[optionIndex].value)
  }

  const getInitialSelectedOption = () => {
    if (!props.options.enumOptions) {
      return null
    }
    const optionIndex = props.options.enumOptions.findIndex((e) => e.value === props.value)
    if (optionIndex === -1) {
      return null
    }
    const option = props.options.enumOptions[optionIndex]
    return { id: `${optionIndex}`, label: option.label }
  }

  return (
    <SelectDropdown
      className={classNames('form-control', { 'disabled-contact-information-field border-0': props.disabled })}
      options={options}
      placeholder='Not set'
      initialSelectedOption={getInitialSelectedOption()}
      onOptionSelected={handleOptionSelected}
      disabled={props.disabled}
      hideArrows={props.disabled}
    />
  )
}

export const CustomCheckboxWidget = (props: WidgetProps) => {
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    props.onChange(e.target.checked)
  }
  return (
    <div
      className={classNames('form-control border-0', {
        'disabled-contact-information-field': props.disabled,
      })}
    >
      <Form.Check
        className='d-flex contact-information-checkbox'
        type='checkbox'
        checked={props.value ?? false}
        onChange={handleChange}
        disabled={props.disabled}
      />
    </div>
  )
}

export const getSchemaEmptyValue = (schema: RJSFSchema) => {
  if (schema.type === 'string') {
    return ''
  }

  if (schema.type === 'number' || schema.type === 'integer') {
    return 0
  }

  if (schema.type === 'boolean') {
    return false
  }
}
